import {
  CalendarOutlined,
  EyeOutlined,
  FilterOutlined,
  SolutionOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Empty, Table, Tag } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, TableConfigure } from '../../../../../core/components';
import { ITableColumnType } from '../../../../../core/interfaces';
import { route } from '../../../../../constants/routes';
import { CandidatesService } from '../../candidate/services/candidates.service';
import { JobApplication } from '../models/job-application.model';
import { formatStageLabel } from '../utils/pipeline-board.utils';

type StatusTone =
  | 'blue'
  | 'cyan'
  | 'gold'
  | 'purple'
  | 'green'
  | 'red'
  | 'default';

export function getCandidateApplicationStatus(
  application: JobApplication
): string {
  const history = application.jobApplicationStatuses || [];
  const latest = history[history.length - 1];
  return (
    application.status ||
    latest?.statusName ||
    latest?.status?.name ||
    (application.closed ? 'Closed' : 'Received')
  );
}

export function getCandidateApplicationStatusTone(status: string): StatusTone {
  const normalized = status.toLowerCase().replace(/[\s-]+/g, '_');
  if (normalized.includes('interview')) return 'purple';
  if (normalized.includes('follow')) return 'gold';
  if (normalized.includes('accept') || normalized.includes('offer')) {
    return 'green';
  }
  if (
    normalized.includes('reject') ||
    normalized.includes('withdraw') ||
    normalized.includes('closed')
  ) {
    return 'red';
  }
  if (normalized.includes('review') || normalized.includes('screen')) {
    return 'cyan';
  }
  if (normalized.includes('receive') || normalized.includes('applied')) {
    return 'blue';
  }
  return 'default';
}

function formatApplicationDate(value?: string) {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.split('T')[0];
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

const JobApplicationTableByCandidate: React.FC = () => {
  const [data, setData] = useState<JobApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [sort, setSort] = useState<string>();
  const [filter, setFilter] = useState<string>();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const getApplications = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(false);
    const params = {
      id,
      page: pagination.current,
      size: pagination.pageSize,
      ...(sort ? { sort } : {}),
      ...(filter ? { filter } : {}),
    };
    try {
      const result = await CandidatesService.GetApplicationsByCandidate(
        id,
        params
      );
      if (result) {
        setData(result.content || []);
        setTotal(result.totalElements);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [pagination, sort, filter, id]);

  useEffect(() => {
    void getApplications();
  }, [getApplications]);

  const openApplication = useCallback(
    (application: JobApplication) => {
      navigate(route(`/job-applications/${application.id}`), {
        state: {
          jobName: application.jobName,
          candidateName: application.candidate,
        },
      });
    },
    [navigate]
  );

  const columns = useMemo<ITableColumnType<JobApplication>[]>(
    () => [
      {
        title: 'Position',
        dataIndex: 'jobName',
        key: 'jobName',
        filter: true,
        sorter: true,
        align: 'left',
        render: (jobName: string, record: JobApplication) => (
          <div className="candidate-application-position">
            <span>
              <SolutionOutlined />
            </span>
            <div>
              <strong>{jobName || record.job?.name || 'Untitled job'}</strong>
              <small>Application record</small>
            </div>
          </div>
        ),
      },
      {
        title: 'Client',
        dataIndex: 'client',
        key: 'client',
        filter: true,
        sorter: true,
        render: (client: string) => client || '—',
      },
      {
        title: 'Recruiter',
        dataIndex: 'recruiter',
        key: 'recruiter',
        filter: true,
        sorter: true,
        render: (recruiter: string, record: JobApplication) => {
          const name = recruiter || record.ownerFullname || 'Unassigned';
          return (
            <div className="candidate-application-recruiter">
              <Avatar size={28}>{name.charAt(0).toUpperCase()}</Avatar>
              <span>{name}</span>
            </div>
          );
        },
      },
      {
        title: 'Stage',
        key: 'status',
        dataIndex: 'status',
        sorter: true,
        render: (_: string, record: JobApplication) => {
          const status = getCandidateApplicationStatus(record);
          return (
            <Tag
              className="candidate-application-stage"
              color={getCandidateApplicationStatusTone(status)}
            >
              {formatStageLabel(status)}
            </Tag>
          );
        },
      },
      {
        title: 'Applied',
        key: 'appliedOn',
        dataIndex: 'appliedOn',
        sorter: true,
        render: (appliedOn: string) => (
          <span className="candidate-application-date">
            <CalendarOutlined />
            {formatApplicationDate(appliedOn)}
          </span>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        width: 110,
        render: (_: unknown, record: JobApplication) => (
          <Button
            icon={<EyeOutlined />}
            onClick={() => openApplication(record)}
          >
            View
          </Button>
        ),
      },
    ],
    [openApplication]
  );

  const { columns: enhancedColumns, data: enhancedData } =
    TableConfigure<JobApplication>({
      columns,
      data,
    });

  const onChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<JobApplication> | SorterResult<JobApplication>[]
  ) => {
    const filterParts: string[] = [];
    for (const key in filters) {
      const value = filters[key]?.[0];
      if (value) filterParts.push(`${key}:like:${value}`);
    }
    setFilter(filterParts.length ? filterParts.join(',') : undefined);

    if (!Array.isArray(sorter) && sorter.order) {
      const direction = sorter.order === 'ascend' ? '' : '-';
      setSort(`${direction}${String(sorter.field)}`);
    } else {
      setSort(undefined);
    }

    setPagination({
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    });
  };

  const openOnPage = data.filter((application) => !application.closed).length;
  const interviewOnPage = data.filter((application) =>
    getCandidateApplicationStatus(application)
      .toLowerCase()
      .includes('interview')
  ).length;

  return (
    <Container>
      <section className="candidate-section candidate-applications">
        <header className="candidate-section__header">
          <div>
            <span className="candidate-section__eyebrow">Hiring history</span>
            <h2>Job Applications</h2>
            <p>
              Review every role, recruiter, stage, and application date from one
              timeline.
            </p>
          </div>
          <span className="candidate-applications__filter-hint">
            <FilterOutlined />
            Filter any column
          </span>
        </header>

        <div className="candidate-applications__summary">
          <div>
            <span>
              <SolutionOutlined />
            </span>
            <p>
              <strong>{total}</strong>
              <small>Total applications</small>
            </p>
          </div>
          <div>
            <span>
              <TeamOutlined />
            </span>
            <p>
              <strong>{openOnPage}</strong>
              <small>Active applications</small>
            </p>
          </div>
          <div>
            <span>
              <CalendarOutlined />
            </span>
            <p>
              <strong>{interviewOnPage}</strong>
              <small>At interview stage</small>
            </p>
          </div>
        </div>

        {loadError ? (
          <div className="candidate-section__error">
            <div>
              <strong>Applications are temporarily unavailable</strong>
              <span>Your candidate profile is still available.</span>
            </div>
            <Button onClick={() => void getApplications()}>Try again</Button>
          </div>
        ) : null}

        <div className="candidate-table-shell">
          <Table<JobApplication>
            columns={enhancedColumns}
            dataSource={enhancedData}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: true,
              showTotal: (count, range) =>
                `${range[0]}–${range[1]} of ${count} applications`,
            }}
            onChange={onChange}
            onRow={(record) => ({
              onDoubleClick: () => openApplication(record),
            })}
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No job applications found for this candidate"
                />
              ),
            }}
          />
        </div>
      </section>
    </Container>
  );
};

export default JobApplicationTableByCandidate;
